import React, { useState, useCallback, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLazyGetRetailDetailPdfQuery } from '../../../api/endpoints/parts';
import RetailSalesTable from './components/RetailSalesTable';
import RetailSalesDetailDialog from './components/RetailSalesDetailDialog';
import RetailCancelDialog from './components/RetailCancelDialog';
import useRetailSales from './hooks/useRetailSales';
import useRetailPendingApproval from './hooks/useRetailPendingApproval';
import useRetailPendingCancel from './hooks/useRetailPendingCancel';
import RetailSalesPdfForm from './utils/RetailSalesPdfForm';

const RetailSalesPage = () => {
    const { th } = useLanguage();
    const { t } = useTranslation();
    const toast = useRef(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);

    const [getRetailDetailPdf] = useLazyGetRetailDetailPdfQuery();

    const {
        customerType,
        setCustomerType,
        data,
        totalCount,
        loading,
        lazyParams,
        onPage,
        onSort,
        refetch
    } = useRetailSales();

    const pendingApproval = useRetailPendingApproval();
    const pendingCancel = useRetailPendingCancel();

    // Detay dialog'unun tüm açılış parametreleri tek bir state objesinde tutuluyor.
    // customerType: Onay/iptal onayı sekmeleri sadece bayiler-arası satış olduğundan 1 (Bayi) zorlanır;
    //               Satışlarım sekmesi/Create için kullanıcının radio seçimi geçerlidir.
    const [detailDialog, setDetailDialog] = useState({
        visible: false,
        retailId: null,
        isCreateMode: false,
        isApprovalMode: false,
        isCancelApprovalMode: false,
        cancelInfo: null,
        customerType: 0
    });

    const [cancelDialog, setCancelDialog] = useState({ visible: false, mode: 'request', data: null });

    const handleViewDetail = useCallback((row) => {
        setDetailDialog({
            visible: true,
            retailId: row.id,
            isCreateMode: false,
            isApprovalMode: false,
            isCancelApprovalMode: false,
            cancelInfo: null,
            customerType: customerType
        });
    }, [customerType]);

    const handleViewPendingDetail = useCallback((row) => {
        setDetailDialog({
            visible: true,
            retailId: row.id,
            isCreateMode: false,
            isApprovalMode: true,
            isCancelApprovalMode: false,
            cancelInfo: null,
            customerType: 1
        });
    }, []);

    const handleViewCancelPendingDetail = useCallback((row) => {
        setDetailDialog({
            visible: true,
            retailId: row.id,
            isCreateMode: false,
            isApprovalMode: false,
            isCancelApprovalMode: true,
            cancelInfo: {
                cancelRequestNote: row.cancelRequestNote,
                cancelRequestedDate: row.cancelRequestedDate,
                cancelRequestedName: row.cancelRequestedName
            },
            customerType: 1
        });
    }, []);

    const handleAdd = useCallback(() => {
        setDetailDialog({
            visible: true,
            retailId: null,
            isCreateMode: true,
            isApprovalMode: false,
            isCancelApprovalMode: false,
            cancelInfo: null,
            customerType: customerType
        });
    }, [customerType]);

    const handleDialogHide = useCallback(() => {
        setDetailDialog((d) => ({
            ...d,
            visible: false,
            retailId: null,
            isCreateMode: false,
            isApprovalMode: false,
            isCancelApprovalMode: false,
            cancelInfo: null
        }));
    }, []);

    const handleTabChange = useCallback((e) => {
        const newIndex = e.index;
        setActiveTabIndex(newIndex);
        // Sekme değişiminde aktif sekmenin verisini yenile (cache'ten gelen eski/yığılmış veri sorunu için).
        if (newIndex === 0) refetch();
        else if (newIndex === 1) pendingApproval.refetch();
        else if (newIndex === 2) pendingCancel.refetch();
    }, [refetch, pendingApproval, pendingCancel]);

    const handleSuccess = useCallback(() => {
        refetch();
        pendingApproval.refetch();
        pendingCancel.refetch();
    }, [refetch, pendingApproval, pendingCancel]);

    const handleRequestCancel = useCallback((row) => {
        setCancelDialog({ visible: true, mode: 'request', data: row });
    }, []);

    const handleCancelDialogHide = useCallback(() => {
        setCancelDialog({ visible: false, mode: 'request', data: null });
    }, []);

    const handleCancelDialogSuccess = useCallback(() => {
        refetch();
        pendingApproval.refetch();
        pendingCancel.refetch();
    }, [refetch, pendingApproval, pendingCancel]);

    const handleDownloadPdf = useCallback(async (row) => {
        if (!row?.id) return;
        setDownloadingPdfId(row.id);
        try {
            const detail = await getRetailDetailPdf({ RetailId: row.id }).unwrap();
            if (!detail) {
                return;
            }
            const pdfTranslations = {
                title: t('retailSales.pdf.title'),
                invoiceSeriNo: t('retailSales.invoiceSeriNo'),
                printDate: t('retailSales.pdf.printDate'),
                invoiceDate: t('retailSales.invoiceDate'),
                maturityDate: t('retailSales.maturityDate'),
                customer: t('retailSales.customer'),
                invoiceAddress: t('retailSales.invoiceAddress'),
                warehouse: t('retailSales.warehouse'),
                paymentMethod: t('retailSales.paymentMethod'),
                approveStatus: t('retailSales.approveStatus'),
                createdBy: t('retailSales.pdf.createdBy'),
                partCode: t('retailSales.partCode'),
                partName: t('retailSales.partName'),
                unit: t('retailSales.unit'),
                quantity: t('retailSales.quantity'),
                unitPrice: t('retailSales.unitPrice'),
                discountType: t('retailSales.discountType'),
                discountRate: t('retailSales.discountRate'),
                sellingPrice: t('retailSales.sellingPrice'),
                discountTypeNone: t('retailSales.pdf.discountTypeNone'),
                discountTypePercent: t('retailSales.pdf.discountTypePercent'),
                discountTypeAmount: t('retailSales.pdf.discountTypeAmount'),
                totalAmount: t('retailSales.totalAmount'),
                grossTotalAmount: t('retailSales.grossTotalAmount'),
                totalDiscount: t('retailSales.totalDiscount'),
                kdvliTutar: t('retailSales.pdf.kdvliTutar'),
                kdvNote: t('retailSales.kdvNote'),
                // ApproveStatus int -> label (6 dilde mevcut çevirilerden besleniyor)
                approveStatus0: t('retailSales.status.0'),
                approveStatus1: t('retailSales.status.1'),
                approveStatus2: t('retailSales.status.2'),
                approveStatus3: t('retailSales.status.3')
            };
            const pdfForm = new RetailSalesPdfForm();
            const pdfDoc = pdfForm.generate(detail, pdfTranslations);
            const fileName = `RetailSale_${detail.invoiceSeriNo || row.id}.pdf`;
            pdfDoc.download(fileName);
        } catch {
            // Hata mesajı baseQueryWithReauth tarafından global olarak gösteriliyor
        } finally {
            setDownloadingPdfId(null);
        }
    }, [getRetailDetailPdf, t]);

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('retailSales.pageTitle')}</h1>
            </div>

            <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
                <TabPanel header={th('retailSales.myRetailSales')}>
                    <div className="grid mb-3 align-items-end">
                        <div className="col-12 md:col-8">
                            <div className="flex align-items-center gap-4">
                                <div className="flex align-items-center gap-2" style={{ height: 40 }}>
                                    <RadioButton 
                                        inputId="rbCustomer" 
                                        name="customerType" 
                                        value={0} 
                                        checked={customerType === 0} 
                                        onChange={(e) => setCustomerType(e.value)} 
                                    />
                                    <label htmlFor="rbCustomer" className="mb-0">{th('retailSales.customerTypeCustomer')}</label>
                                    <RadioButton 
                                        inputId="rbDealer" 
                                        name="customerType" 
                                        value={1} 
                                        checked={customerType === 1} 
                                        onChange={(e) => setCustomerType(e.value)} 
                                    />
                                    <label htmlFor="rbDealer" className="mb-0">{th('retailSales.customerTypeDealer')}</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 md:col-4 flex justify-content-end">
                            <Button 
                                label={th('common.add')} 
                                icon="pi pi-plus" 
                                severity="success" 
                                onClick={handleAdd} 
                            />
                        </div>
                    </div>

                    <RetailSalesTable
                        th={th}
                        data={data}
                        totalRecords={totalCount}
                        lazyParams={lazyParams}
                        onPage={onPage}
                        onSort={onSort}
                        loading={loading}
                        onViewDetail={handleViewDetail}
                        onDownloadPdf={handleDownloadPdf}
                        downloadingPdfId={downloadingPdfId}
                        onRequestCancel={handleRequestCancel}
                    />
                </TabPanel>

                <TabPanel header={th('retailSales.pendingApprovals')}>
                    <RetailSalesTable
                        th={th}
                        data={pendingApproval.data}
                        totalRecords={pendingApproval.totalCount}
                        lazyParams={pendingApproval.lazyParams}
                        onPage={pendingApproval.onPage}
                        onSort={pendingApproval.onSort}
                        loading={pendingApproval.loading}
                        onViewDetail={handleViewPendingDetail}
                        onDownloadPdf={handleDownloadPdf}
                        downloadingPdfId={downloadingPdfId}
                    />
                </TabPanel>

                <TabPanel header={th('retailSales.pendingCancelApprovals')}>
                    <RetailSalesTable
                        th={th}
                        data={pendingCancel.data}
                        totalRecords={pendingCancel.totalCount}
                        lazyParams={pendingCancel.lazyParams}
                        onPage={pendingCancel.onPage}
                        onSort={pendingCancel.onSort}
                        loading={pendingCancel.loading}
                        onViewDetail={handleViewCancelPendingDetail}
                        onDownloadPdf={handleDownloadPdf}
                        downloadingPdfId={downloadingPdfId}
                        showCancelInfo
                        variant="cancelApproval"
                    />
                </TabPanel>
            </TabView>

            <RetailSalesDetailDialog
                visible={detailDialog.visible}
                onHide={handleDialogHide}
                retailId={detailDialog.retailId}
                isCreateMode={detailDialog.isCreateMode}
                isApprovalMode={detailDialog.isApprovalMode}
                isCancelApprovalMode={detailDialog.isCancelApprovalMode}
                customerType={detailDialog.customerType}
                cancelInfo={detailDialog.cancelInfo}
                onSuccess={handleSuccess}
            />

            <RetailCancelDialog
                visible={cancelDialog.visible}
                mode={cancelDialog.mode}
                retailData={cancelDialog.data}
                onHide={handleCancelDialogHide}
                onSuccess={handleCancelDialogSuccess}
            />
        </div>
    );
};

export default RetailSalesPage;
